import React, { useMemo } from 'react';
import { BsExclamationTriangle } from 'react-icons/bs';
import { useSelector } from 'react-redux';
import { Box, HStack, Icon, IconButton, Tooltip } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import StatusButton from '../../../../../../../../../../../components/StatusButton';
import { getStatusButtonMenuOptions } from '../../../../../../../../../../../components/StatusButton/reactQueryUtil';
import { CypressIds } from '../../../../../../../../../../../constants/cypressIds';
import { InsuredProfilePageDuskIds } from '../../../../../../../../../../../constants/duskIds';
import { Statuses } from '../../../../../../../../../../../constants/statuses';
import { selectPolicyTypeIdIsLoading } from '../../../../../../../../../../../redux/modules/insuredProfilePage';
import { useArchivePolicy, useDeletePolicy, useReactivatePolicy } from '../../../../../../../../../../../services/policy-service';
import { useUpdateRecordRequirements } from '../../../../../../../../../../../services/record-service';
import { isPast } from '../../../../../../../../../../../utils/dateUtil';

const StatusOptions = ({ policy, recordId, insuredId, jobId }) => {
  const { mutate: updateRequirementStatus } = useUpdateRecordRequirements({ recordId: parseInt(recordId, 10), insuredId: parseInt(insuredId, 10) });
  const { mutate: reactivatePolicy } = useReactivatePolicy({ policyId: policy.id, insuredId, jobId, recordId });
  const { mutate: archivePolicy } = useArchivePolicy({ policyId: policy.id, insuredId, jobId, recordId });
  const { mutate: deletePolicy } = useDeletePolicy({ policyId: policy.id, insuredId, jobId, recordId });

  const policyTypeIsLoading = useSelector(selectPolicyTypeIdIsLoading(policy.insurancePolicyTypeId));

  const isNotReceived = !policy.id;

  const requirementOptions = isNotReceived
    ? getStatusButtonMenuOptions(policy.requirementStatus, 'notReceivedRequirements')
    : getStatusButtonMenuOptions(policy.requirementStatus, 'requirements');

  const isDisabled = policy && policy.isInheritable
    && policy.insureds?.length
    && !policy.insureds.find(p => p.insuredId === insuredId);

  const duskSelector = useMemo(() => InsuredProfilePageDuskIds.getPolicyRequirementStatusButton(
    policy.insurancePolicyTypeId,
    policy.id
  ), [policy]);

  if (isDisabled) {
    return (
      <Tooltip
        label="You cannot edit an inherited policy"
        variant="light"
      >
        <Box>
          <StatusButton
            cypressId={CypressIds.policyStatusButton(policy.insurancePolicyTypeId, policy.id)}
            disabled={isDisabled}
            duskSelector={duskSelector}
            id={policy.id}
            options={requirementOptions}
          />
        </Box>
      </Tooltip>
    );
  }

  return (
    <HStack spacing={2}>
      {(!isPast(policy.expiresAt) && policy.requirementStatus === Statuses.Expired && !policyTypeIsLoading) && (
        <Box>
          <Tooltip
            label="This policy has not expired, but there is another active policy of this type that has expired.
          Please archive or delete the expired policy in order to take actions on this requirement."
            variant="light"
          >
            <Box>
              <IconButton
                aria-label="false_expired_icon"
                icon={<Icon as={BsExclamationTriangle} />}
                size="sm"
                variant="textError"
              />
            </Box>
          </Tooltip>
        </Box>
      )}

      <StatusButton
        cypressId={CypressIds.policyStatusButton(policy.insurancePolicyTypeId, policy.id)}
        duskSelector={duskSelector}
        isLoading={policy?.statusLoading || policyTypeIsLoading}
        metadata={{
          recordId,
          insuredId,
          jobId,
          policyTypeId: policy.insurancePolicyTypeId,
          policyId: policy.id,
          itemIds: policy?.itemIds,
          updateRequirementStatus,
          reactivatePolicy,
          archivePolicy,
          deletePolicy
        }}
        options={requirementOptions}
      />
    </HStack>
  );
};

StatusOptions.propTypes = {
  policy: PropTypes.shape({
    id: PropTypes.number,
    insurancePolicyTypeId: PropTypes.number,
    isInheritable: PropTypes.bool,
    insureds: PropTypes.arrayOf(PropTypes.shape({})),
    status: PropTypes.string,
    requirementStatus: PropTypes.string,
    expiresAt: PropTypes.string,
    statusLoading: PropTypes.bool,
    itemIds: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  recordId: PropTypes.number.isRequired,
  insuredId: PropTypes.number.isRequired,
  jobId: PropTypes.number.isRequired
};

export default StatusOptions;
